// The tasks a new trial gets, as the API makes them when the trial is created (TrialModelObserver::created in
// agromate-api): Visit 00 plus one visit per Visit Count (00 ... N, as in TIS; 0 gives only Visit 00),
// Application A, B, ... for Application Count (at least one; letters stop at Z), and an activity for every
// catalog field that is on (Yes or To be decided).
import { timeIntervalParts } from './taskStatus';

const MAX_APPLICATIONS = 26;

/** Whether an activity field is on, as the API reads it (TrialModelObserver::isEnabled). */
export const isActivityOn = (value) => !!value && value !== '0' && value !== 0;

const count = (value, max = Infinity) => Math.min(max, Math.max(1, parseInt(value, 10) || 0));

/**
 * The new trial's tasks in groups ({ title, tasks: [{ vano, name }] }): Visits, Applications, then the
 * activities under their catalog headings (`categories`: utils/vanoCodes.js ACTIVITY_CATEGORIES). Groups
 * without tasks are left out.
 */
export function wizardTaskGroups(trialForm, vanoCodes, categories) {
  const visits = Array.from({ length: Math.max(0, parseInt(trialForm.VisitCount, 10) || 0) + 1 }, (_, i) => {
    const vano = String(i).padStart(2, '0');
    return { vano, name: `Visit ${vano}` };
  });
  const applications = Array.from({ length: count(trialForm.ApplicationCount, MAX_APPLICATIONS) }, (_, i) => {
    const vano = String.fromCharCode(65 + i);
    return { vano, name: `Application ${vano}` };
  });
  const activities = categories.map((category) => ({
    title: category.title,
    tasks: vanoCodes
      .filter((code) => (code.category ?? null) === category.key && isActivityOn(trialForm[code.field]))
      .map((code) => ({ vano: code.vano, name: code.label || code.task })),
  }));
  return [{ title: 'Visits', tasks: visits }, { title: 'Applications', tasks: applications }, ...activities]
    .filter((group) => group.tasks.length > 0);
}

// A visit's number as the API makes it ("3" in old data is Visit 03)
const vanoOf = (vano) => {
  const v = String(vano ?? '').trim();
  return /^\d$/.test(v) ? v.padStart(2, '0') : v;
};
const isCancelled = (task) => task?.cancelled === true || task?.cancelled === 1 || task?.cancelled === '1';

/**
 * The Time intervals a copy takes from the source trial's tasks (customer point 21.1), by VA No: { dates, texts } as
 * diary_dates and diary_texts - each task's date and text as timeIntervalParts reads them ("Not needed" too). Planned
 * and Done are the source trial's own and are never taken. Two tasks with one VA No (old data): the one not
 * cancelled, then the newest.
 */
export function copiedIntervals(diaries) {
  const chosen = new Map();
  [...(diaries || [])]
    .sort((a, b) => Number(isCancelled(b)) - Number(isCancelled(a)) || (Number(a?.id) || 0) - (Number(b?.id) || 0))
    .forEach((task) => {
      const { date, text } = timeIntervalParts(task);
      if (date || text) chosen.set(vanoOf(task.vano), { date, text });
    });
  const pick = (part) => Object.fromEntries([...chosen].filter(([, v]) => v[part]).map(([vano, v]) => [vano, v[part]]));
  return { dates: pick('date'), texts: pick('text') };
}

/** The dates (or texts, trimmed) to send with the new trial (diary_dates): only those of tasks it gets, by VA No. */
export function diaryDatesFor(groups, dates) {
  return Object.fromEntries(groups.flatMap((group) => group.tasks)
    .map((task) => [task.vano, String(dates[task.vano] ?? '').trim()])
    .filter(([, value]) => value));
}
